import type { OnFilterSheetCloseDetail, OnFilterSheetOpenDetail } from "./FilterSheetTopics";
import type { DresonRuleResolvedEvent } from "../multifiltering/FilterTypes";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { filterSheetButton, localNavigation } from "./Selectors";
import { OverflowBox } from "../overflowBox/OverflowBox";
import { Filter } from "../filter/Filter";
import { FilterSection } from "../filter/FilterSection";
import { events } from "../events";
import { eventQBus } from "../types/EventQBus";
import OcFilterSheet, { NBF_SHEET_CHANGE_TRACKING_LABELS, SECOND_LEVEL_CHANGE_TRACKING_LABELS } from "./OcFilterSheet";
import OcFilterDetailSheet from "./OcFilterDetailSheet";
import { getOcActiveSheet } from "../util/NonModuleFunctions";
import OcFilterNavigationSheet, { LOCAL_NAVIGATION } from "./OcFilterNavigationSheet";
import { isMobile } from "../util/Breakpoint";
import { listFeatures, readLegacyLabel, readLegacyLabels, submitActionTrackingFrom } from "../tracking/FeatureTracking";
import { sanFoldout } from "./FilterSheetTracking";
import { trackNba } from "../nextBestAction/NextBestActionComponent";
import { getActiveVariation } from "../experiment/Experiments";
import { isActive } from "../toggle/Toggle";
import { childElements } from "../util/Utils";
import { FilterTitle } from "../filter/FilterTitle";
import { enterSecondLevel, exitSecondLevel, isSecondLevelNbf as isAllLevelNbf } from "./AllLevelSheet";
import type { SelectionState } from "@otto-ec/nexus-registry/selection";

function setInitialScrollPosition(scrollOffset?: number) {
  const elem = filterSheetButton();
  if (elem) {
    if (scrollOffset) {
      elem.dataset.scrollOffset = scrollOffset.toString();
    } else {
      delete elem.dataset.scrollOffset;
    }
  }
}

function onFilterSheetOpen() {
  const ocSheet = OcFilterSheet.pick();
  ocSheet.open();
  return;
}

function addSheetListeners() {
  OcFilterSheet.pick().addListeners();
}

function addNavigationSheetListeners() {
  OcFilterNavigationSheet.pick().addListeners();
}

function addDetailSheetListeners() {
  OcFilterDetailSheet.addListeners();
}

function onSheetChanged() {
  OverflowBox.factory.all(OcFilterSheet.pick().sheet).map((elem) => elem.initializeVisibilities());
}

function onFilterSheetClose() {
  const activeSheet = getOcActiveSheet();
  activeSheet?.close();
  /*                                          */
  const activeSheetContent = activeSheet?.querySelector(
    `.heureka_filterSheet__content, .heureka_filterDetailSheet__content`,
  );
  emitFilterSheetClosedEvent(activeSheetContent);
  OcFilterSheet.pick().close();
}

function emitFilterSheetClosedEvent(sheetContent?: HTMLElement) {
  const { filterId, tsLabels = "{}" } = sheetContent?.dataset || {};
  const san_FilterWhatIf = readLegacyLabel(filterSheetButton(), "san_FilterWhatIf");
  eventQBus.emit("ftfind.filterSheet.closed", {
    filterId,
    tracking: {
      san_Foldout: sanFoldout(undefined),
      san_FilterWhatIf,
      ...JSON.parse(tsLabels),
    },
  });
}

function onFilterSheetClosed() {
  setInitialScrollPosition();
}

function onNextBestFilterSheetOpen(event: OnFilterSheetOpenDetail) {
  const { filterId } = event;
  const filter = Filter.filterId(filterId);
  if (filter && filter.form.parentElement) {
    const tracking: Partial<TrackingLabels> = {
      san_FilterFoldoutType: filterId,
      san_FilterFoldoutActivity: "open_nbf",
      ...readLegacyLabels(filter.form),
    };
    filter?.updateChildrenFeatureStatus(true);
    submitActionTrackingFrom(filter.form.parentElement, "open", tracking, listFeatures(["Filter-Overview"]));
  }

  if (isActive("HEUREKA_2398_ALL_LEVEL_SHEET")) {
    OcFilterSheet.pick()?.open();
    enterSecondLevel(filterId, true);
    eventQBus.emit("ftfind.filterSheet.openedDetail", { filterId });
  } else {
    OcFilterDetailSheet.createForFilter(filterId).setNextBestFilterSheet().initializeNextBestFilterSheet();
  }
  Filter.updateChangeTrackingLabels(filterId, NBF_SHEET_CHANGE_TRACKING_LABELS);
}

function onFilterSheetOpenDetail(event: OnFilterSheetOpenDetail) {
  const { filterId } = event;
  memorizeScrollPositionLevel1();
  if (isActive("HEUREKA_2398_ALL_LEVEL_SHEET")) {
    /*                                                                                            */
    enterSecondLevel(filterId, false);
    eventQBus.emit("ftfind.filterSheet.openedDetail", { filterId });
  } else {
    OcFilterDetailSheet.createForFilter(filterId).setSheet().initializeSheet();
  }
  Filter.updateChangeTrackingLabels(filterId, SECOND_LEVEL_CHANGE_TRACKING_LABELS);
}

function memorizeScrollPositionLevel1() {
  const sheetContent = OcFilterSheet.pick().sheet as HTMLElement;
  const scrollOffset = sheetContent?.scrollTop;
  setInitialScrollPosition(scrollOffset);
  return scrollOffset;
}

function onFilterSheetCloseDetail(event: OnFilterSheetCloseDetail) {
  const { filterId } = event;

  if (isActive("HEUREKA_2398_ALL_LEVEL_SHEET")) {
    /*                                                                                         */
    /*                                                                                        */
    const wasNbf = isAllLevelNbf();
    exitSecondLevel();
    eventQBus.emit("ftfind.filterSheet.closedDetail", { filterId });
    if (isActive("HEUREKA_2388_FIX_EMPTY_DETAILSHEET_AFTER_BROWSER_BACK") && wasNbf) {
      return;
    }
    OcFilterSheet.dropPopularValuesOrder();
    OcFilterSheet.scrollToRecentFilter(event.filterId);
    return;
  }

  /*                                                                           */
  const wasNbf = OcFilterDetailSheet.isCurrentlyNbf();
  OcFilterDetailSheet.createForFilter(filterId).back().emitFilterSheetDetailClosedEvent();
  /*                                                              */
  if (isActive("HEUREKA_2388_FIX_EMPTY_DETAILSHEET_AFTER_BROWSER_BACK") && wasNbf) {
    return;
  }
  OcFilterSheet.dropPopularValuesOrder();
  OcFilterSheet.pick().open();
  OcFilterSheet.scrollToRecentFilter(event.filterId);
}

function onFilterSheetOpenNavigation() {
  memorizeScrollPositionLevel1();
  OcFilterNavigationSheet.pick().setSheet().initializeSheet();
}

function onDresonRuleResolve() {
  OcFilterSheet.declareIfOpen()?.lockContent();
}

function onDresonRuleResolvedDeprecated(data: DresonRuleResolvedEvent) {
  const currentDresonRule = FilterSection.template().currentDresonRule;
  if (data.count == 0 || data.rule === currentDresonRule) {
    /*                 */
    /*                                                                                              */
    /*                                                     */
    setTimeout(() => {
      OcFilterSheet.declareIfOpen()?.unlockContent();
    }, 0);
  }
}

function onDresonRuleResolved(data?: SelectionState) {
  const currentDresonRule = FilterSection.template()?.currentDresonRule;
  if (data?.rule === currentDresonRule) {
    /*                 */
    /*                                                                                              */
    /*                                                     */
    setTimeout(() => {
      OcFilterSheet.declareIfOpen()?.unlockContent();
    }, 0);
  }
}

function onDresonRuleResolveAborted() {
  OcFilterSheet.addErrorsOnFilterTitleValues();
  OcFilterSheet.declareIfOpen()?.unlockContent();
}

function onFilterSectionLoaded() {
  if (isMobile()) {
    const scrollOffset = memorizeScrollPositionLevel1();
    OcFilterSheet.addLocalNavigationTitleValue();
    OcFilterSheet.addErrorsOnFilterTitleValues();
    OcFilterSheet.initPartialLocking();

    OcFilterSheet.commitPopularValuesOrder();
    const ocFilterSheet = OcFilterSheet.declareIfOpen();
    if (ocFilterSheet) {
      ocFilterSheet.updateContent().unlockContent().setScrollOffset(scrollOffset);
    } else {
      if (isActive("HEUREKA_2398_OPTIMIZE_FILTER_SHEET_PERFORMANCE")) {
        OcFilterSheet.declare()?.removeContent()?.updateContent()?.unlockContent();
      } else {
        OcFilterSheet.declare()?.removeContent()?.unlockContent();
      }
    }
  }
}

function onFilterSectionLoadAborted() {
  OcFilterSheet.addInitialLoadError();
  OcFilterSheet.addLocalNavigationTitleValue();
  OcFilterSheet.declareIfOpen()?.updateContent().unlockContent();
}

function onLocalNavigationLoaded() {
  OcFilterSheet.addLocalNavigationTitleValue();
}

function isLocalNavigationPresent() {
  const htmlElements = childElements(localNavigation());
  const localNavigationChildren = htmlElements || [];
  return localNavigationChildren.length > 0;
}

function checkLocalNavigationPresence() {
  if (!isLocalNavigationPresent()) {
    FilterTitle.filterId(LOCAL_NAVIGATION)?.remove();
  }
}

export function registerOcFilterSheetHandlers() {
  eventQBus.on("ftfind.filterSheet.open", onFilterSheetOpen);
  eventQBus.on("ftfind.filterSheet.opened", onSheetChanged);
  eventQBus.on("ftfind.filterSheet.close", onFilterSheetClose);
  eventQBus.on("ftfind.filterSheet.closed", onFilterSheetClosed);

  if (
    getActiveVariation("e3792", "StatusQuo") !== "StatusQuo" ||
    getActiveVariation("e3911", "StatusQuo") !== "StatusQuo"
  ) {
    eventQBus.on("ftfind.filterSheet.openNextBestFilterDetail", onNextBestFilterSheetOpen);
  }
  eventQBus.on("ftfind.filterSheet.openDetail", onFilterSheetOpenDetail);
  eventQBus.on("ftfind.filterSheet.closeDetail", onFilterSheetCloseDetail);
  eventQBus.on("ftfind.filterSheet.closedDetail", onSheetChanged);
  /*                                                                                                                                   */
  eventQBus.on("ftfind.filterSheet.openedDetail", trackNba);
  if (isActive("HEUREKA_2178_FILTER_SECTION_IS_NOT_LOADED")) {
    events.onceFilterSectionInitialLoaded(addSheetListeners);
    if (isActive("HEUREKA_2388_FIX_EMPTY_DETAILSHEET_AFTER_BROWSER_BACK")) {
      events.onceFilterSectionInitialLoaded(addDetailSheetListeners);
    }
  } else {
    eventQBus.once("heureka.filterSection.initialLoaded", addSheetListeners);
    if (isActive("HEUREKA_2388_FIX_EMPTY_DETAILSHEET_AFTER_BROWSER_BACK")) {
      eventQBus.once("heureka.filterSection.initialLoaded", addDetailSheetListeners);
    }
  }

  /*         */
  eventQBus.on("ftfind.filterSheet.openNavigation", onFilterSheetOpenNavigation);
  eventQBus.on("ftfind.filterSheet.openedNavigation", onSheetChanged);
  eventQBus.on("ftfind.filterSheet.closedNavigation", onSheetChanged);
  if (isActive("HEUREKA_2178_FILTER_SECTION_IS_NOT_LOADED")) {
    events.onceFilterSectionInitialLoaded(addNavigationSheetListeners);
  } else {
    eventQBus.once("heureka.filterSection.initialLoaded", addNavigationSheetListeners);
  }

  /*      */
  if (isActive("HEUREKA_2407_MIGRATE_TO_REPTILE_NEXUS")) {
    events.onDresonRuleResolve(onDresonRuleResolve);
    events.onDresonRuleResolved(onDresonRuleResolved);
    events.onDresonRuleResolveAborted(onDresonRuleResolveAborted);
  } else {
    eventQBus.on("ftfind.dresonRule.resolve", onDresonRuleResolve);
    eventQBus.on("ftfind.dresonRule.resolved", onDresonRuleResolvedDeprecated);
    eventQBus.on("ftfind.dresonRule.resolveAborted", onDresonRuleResolveAborted);
  }

  /*        */
  events.onFilterSectionLoaded(onFilterSectionLoaded);
  events.onFilterSectionLoadAborted(onFilterSectionLoadAborted);
  eventQBus.on("ftfind.localNavigation.loaded", onLocalNavigationLoaded);
  events.onFilterSectionLoaded(onSheetChanged);
  if (isActive("HEUREKA_2395_REMOVE_LOCAL_NAVIGATION_ON_TOPIC_PAGES")) {
    events.onFilterSectionLoaded(checkLocalNavigationPresence);
  }
}
